/**
 * Vendor logo utility — maps known vendors to their domains for logo fetching.
 * Uses Clearbit Logo API with Google Favicons as fallback.
 */
import { useState } from "react";

// Vendor name patterns → domain mapping
// Keys are lowercase for matching; supports partial/fuzzy matching
const VENDOR_DOMAINS = {
  // Credit Cards / Banks
  "chase": "chase.com",
  "american express": "americanexpress.com",
  "amex": "americanexpress.com",
  "capital one": "capitalone.com",
  "citi": "citi.com",
  "citibank": "citi.com",
  "wells fargo": "wellsfargo.com",
  "bank of america": "bankofamerica.com",
  "discover": "discover.com",
  "barclays": "barclays.com",
  "hsbc": "hsbc.com",
  "usaa": "usaa.com",
  "us bank": "usbank.com",
  "td bank": "td.com",
  "pnc": "pnc.com",
  "synchrony": "synchrony.com",
  "apple card": "apple.com",
  "apple": "apple.com",
  "visa": "visa.com",
  "mastercard": "mastercard.com",
  // Hotels / Accommodations
  "marriott": "marriott.com",
  "hilton": "hilton.com",
  "hyatt": "hyatt.com",
  "ihg": "ihg.com",
  "intercontinental": "ihg.com",
  "holiday inn": "ihg.com",
  "booking.com": "booking.com",
  "booking": "booking.com",
  "airbnb": "airbnb.com",
  "expedia": "expedia.com",
  "hotels.com": "hotels.com",
  "agoda": "agoda.com",
  "vrbo": "vrbo.com",
  "accor": "accor.com",
  "sofitel": "accor.com",
  "novotel": "accor.com",
  "best western": "bestwestern.com",
  "wyndham": "wyndham.com",
  "radisson": "radissonhotels.com",
  "four seasons": "fourseasons.com",
  "ritz carlton": "ritzcarlton.com",
  "ritz-carlton": "ritzcarlton.com",
  "sheraton": "marriott.com",
  "westin": "marriott.com",
  "w hotel": "marriott.com",
  "st. regis": "marriott.com",
  "fairfield": "marriott.com",
  "courtyard": "marriott.com",
  "residence inn": "marriott.com",
  "hampton inn": "hilton.com",
  "hampton": "hilton.com",
  "doubletree": "hilton.com",
  "conrad": "hilton.com",
  "waldorf": "hilton.com",
  "embassy suites": "hilton.com",
  "hostelworld": "hostelworld.com",
  "trip.com": "trip.com",
  // Airlines
  "delta": "delta.com",
  "united": "united.com",
  "american airlines": "aa.com",
  "southwest": "southwest.com",
  "jetblue": "jetblue.com",
  "alaska airlines": "alaskaair.com",
  "alaska air": "alaskaair.com",
  "spirit": "spirit.com",
  "frontier": "flyfrontier.com",
  "air france": "airfrance.com",
  "klm": "klm.com",
  "british airways": "britishairways.com",
  "lufthansa": "lufthansa.com",
  "emirates": "emirates.com",
  "qatar": "qatarairways.com",
  "qatar airways": "qatarairways.com",
  "singapore airlines": "singaporeair.com",
  "cathay pacific": "cathaypacific.com",
  "cathay": "cathaypacific.com",
  "ana": "ana.co.jp",
  "all nippon": "ana.co.jp",
  "jal": "jal.co.jp",
  "japan airlines": "jal.co.jp",
  "korean air": "koreanair.com",
  "asiana": "flyasiana.com",
  "thai airways": "thaiairways.com",
  "eva air": "evaair.com",
  "china airlines": "china-airlines.com",
  "china eastern": "ceair.com",
  "china southern": "csair.com",
  "air china": "airchina.com",
  "turkish airlines": "turkishairlines.com",
  "virgin atlantic": "virginatlantic.com",
  "virgin": "virginatlantic.com",
  "air canada": "aircanada.com",
  "qantas": "qantas.com",
  "ryanair": "ryanair.com",
  "easyjet": "easyjet.com",
  "wizz air": "wizzair.com",
  "tap portugal": "flytap.com",
  "tap": "flytap.com",
  "iberia": "iberia.com",
  "finnair": "finnair.com",
  "sas": "flysas.com",
  "norwegian": "norwegian.com",
  "icelandair": "icelandair.com",
  "air new zealand": "airnewzealand.co.nz",
  "etihad": "etihad.com",
  "air india": "airindia.com",
  "jetstar": "jetstar.com",
  "scoot": "flyscoot.com",
  "peach": "flypeach.com",
  "air premia": "airpremia.com",
  "t'way": "twayair.com",
  "tway": "twayair.com",
  "jin air": "jinair.com",
  "jeju air": "jejuair.net",
  "air busan": "airbusan.com",
  "starlux": "starlux-airlines.com",
  "zipair": "zipair.net",
  // Rail
  "amtrak": "amtrak.com",
  "eurostar": "eurostar.com",
  "trenitalia": "trenitalia.com",
  "renfe": "renfe.com",
  "sncf": "sncf.com",
  "db": "bahn.de",
  "deutsche bahn": "bahn.de",
  "jr": "jreast.co.jp",
  "korail": "letskorail.com",
  "omio": "omio.com",
  "trainline": "thetrainline.com",
  "national rail": "nationalrail.co.uk",
  "cp": "cp.pt",
  // Rideshare / Transport
  "uber": "uber.com",
  "lyft": "lyft.com",
  "grab": "grab.com",
  "bolt": "bolt.eu",
  "gojek": "gojek.com",
  // Cruise
  "royal caribbean": "royalcaribbean.com",
  "carnival": "carnival.com",
  "norwegian cruise": "ncl.com",
  "princess": "princess.com",
  "celebrity": "celebritycruises.com",
  "msc": "msccruises.com",
  "disney cruise": "disneycruise.disney.go.com",
  // Car Rental
  "hertz": "hertz.com",
  "avis": "avis.com",
  "enterprise": "enterprise.com",
  "budget": "budget.com",
  "national car": "nationalcar.com",
  "sixt": "sixt.com",
  "turo": "turo.com",
};

/**
 * Find the domain for a vendor name using fuzzy matching.
 * User-supplied domain overrides (from the Vendors page) take precedence over
 * the static VENDOR_DOMAINS map.
 */
const VENDOR_DOMAIN_OVERRIDES = new Map();

/**
 * Register a user-supplied domain for a vendor name. Usually called once on
 * app boot after fetching the vendors list, and again whenever the user
 * updates a vendor's logo domain from the Vendors page.
 */
export const setVendorDomainOverride = (vendorName, domain) => {
  if (!vendorName) return;
  const key = vendorName.toLowerCase().trim();
  if (domain && domain.trim()) {
    VENDOR_DOMAIN_OVERRIDES.set(key, domain.trim());
  } else {
    VENDOR_DOMAIN_OVERRIDES.delete(key);
  }
};

/**
 * Bulk-register overrides. Safe to call repeatedly (replaces existing entries).
 */
export const registerVendorDomainOverrides = (vendors) => {
  if (!Array.isArray(vendors)) return;
  for (const v of vendors) {
    if (v?.name && v?.logo_domain) {
      setVendorDomainOverride(v.name, v.logo_domain);
    }
  }
};

export const getVendorDomain = (vendorName) => {
  if (!vendorName) return null;
  const lower = vendorName.toLowerCase().trim();

  // User-provided override wins
  if (VENDOR_DOMAIN_OVERRIDES.has(lower)) {
    return VENDOR_DOMAIN_OVERRIDES.get(lower);
  }

  // Exact match against the static map
  if (VENDOR_DOMAINS[lower]) return VENDOR_DOMAINS[lower];

  // Check if vendor name contains a known key (longest match first)
  const keys = Object.keys(VENDOR_DOMAINS).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (lower.includes(key)) return VENDOR_DOMAINS[key];
  }

  return null;
};

/**
 * True when the logo falls back to the grey initial — used by VendorsPage
 * to decide whether to surface a "suggest a logo" CTA for a vendor.
 */
export const isVendorLogoFallback = (vendorName) => {
  if (!vendorName) return true;
  const lower = vendorName.toLowerCase().trim();
  // Local Vendor gets its own fun badge, not a fallback
  if (lower === "local vendor" || lower.startsWith("local vendor ")) return false;
  return !getVendorDomain(vendorName);
};

/**
 * Get the logo URL for a vendor. Returns Clearbit logo URL or null.
 */
export const getVendorLogoUrl = (vendorName) => {
  const domain = getVendorDomain(vendorName);
  if (!domain) return null;
  return `https://logo.clearbit.com/${domain}`;
};

/**
 * Get the favicon URL for a vendor (smaller, more reliable fallback).
 */
export const getVendorFaviconUrl = (vendorName) => {
  const domain = getVendorDomain(vendorName);
  if (!domain) return null;
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
};

/**
 * React component that renders a vendor logo with graceful fallback.
 * Shows: Clearbit logo → Google favicon → styled initial circle
 * Special-case "Local Vendor" renders a distinctive gradient pin badge.
 */
export const VendorLogo = ({ vendorName, size = 24, className = "" }) => {
  const [src, setSrc] = useState(() => getVendorLogoUrl(vendorName));
  const [fallbackLevel, setFallbackLevel] = useState(0);

  if (!vendorName) return null;

  // "Local Vendor" gets a fun branded pin badge so it's visually distinct from
  // the "L" fallback and other generic vendors.
  const lower = vendorName.toLowerCase().trim();
  if (lower === "local vendor" || lower.startsWith("local vendor ")) {
    return (
      <span
        className={`inline-flex items-center justify-center rounded-full flex-shrink-0 ring-1 ring-white/60 shadow-sm ${className}`}
        style={{
          width: size,
          height: size,
          background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 60%, #ec4899 100%)",
        }}
        title="Local Vendor"
        aria-label="Local Vendor"
      >
        <svg
          viewBox="0 0 24 24"
          width={Math.round(size * 0.7)}
          height={Math.round(size * 0.7)}
          fill="none"
          stroke="#fff"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0Z" />
          <circle cx="12" cy="10" r="2.5" fill="#fff" stroke="none" />
        </svg>
      </span>
    );
  }

  const handleError = () => {
    if (fallbackLevel === 0) {
      // Try Google favicon
      const favicon = getVendorFaviconUrl(vendorName);
      if (favicon) {
        setSrc(favicon);
        setFallbackLevel(1);
        return;
      }
    }
    // Show initial circle
    setSrc(null);
    setFallbackLevel(2);
  };

  if (!src || fallbackLevel >= 2) {
    // No logo available — show styled initial
    const initial = vendorName.charAt(0).toUpperCase();
    return (
      <span
        className={`inline-flex items-center justify-center rounded-md bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 font-semibold text-xs flex-shrink-0 ${className}`}
        style={{ width: size, height: size, fontSize: size * 0.45 }}
        title={vendorName}
      >
        {initial}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      className={`rounded-sm object-contain flex-shrink-0 ${className}`}
      onError={handleError}
      loading="lazy"
      style={{ width: size, height: size }}
    />
  );
};
